import { Component, Inject, OnInit, Renderer2 } from "@angular/core";
import { DOCUMENT } from "@angular/common";
import { HttpClient } from "@angular/common/http";

import { getMessaging, onMessage } from "@angular/fire/messaging";
import { getAuth, onAuthStateChanged, signInAnonymously } from "@angular/fire/auth";
import { Store, select } from "@ngrx/store";

import { getCurrentUser } from "./store/selectors";
import { IUser } from "./models";
import { AuthService, MessagingService } from "./services";
import {
  VCFO_ORGANIZATION_ID,
  QUPIL_ORGANIZATION_ID,
  ORGANIZATION_ID,
  QUPIL_LANGUAGE_EN,
  VCFO_LANGUAGE_EN,
} from "./constants";
import { NavigationError, Router } from "@angular/router";
import { loadNotifications } from "./shared-modules/navbar/store/actions";
import { IQuery } from "./shared-modules/navbar/models";
import { AppI18nService } from "./services/app-i18n.service";
import { environment } from "../environments/environment";

@Component({
  selector: "app-root",
  templateUrl: "./app.component.html",
  styleUrls: ["./app.component.scss"],
})
export class AppComponent implements OnInit {
  private messaging;
  private readonly notificationRefreshQuery: IQuery = { page: 1, limit: 7 };
  constructor(
    private message: MessagingService,
    private _renderer2: Renderer2,
    @Inject(DOCUMENT) private _document: Document,
    private authService: AuthService,
    private router: Router,
    private store: Store,
    private appI18n: AppI18nService,
    private http: HttpClient
  ) {}

  async ngOnInit() {
    // Handle chunk load failures (e.g. lazy-loaded Siraj module) — reload once to fetch fresh chunks.
    // Uses sessionStorage gate to prevent infinite reload loops if the chunk is permanently unavailable.
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationError && event.error?.name === 'ChunkLoadError') {
        const key = 'chunk_load_failed';
        if (!sessionStorage.getItem(key)) {
          sessionStorage.setItem(key, '1');
          window.location.assign(event.url);
        } else {
          sessionStorage.removeItem(key);
        }
      }
    });

   // Initialize messaging instance (with error handling for unsupported browsers/guests)
   try {
     this.messaging = getMessaging();
   } catch (error) {
     // Firebase messaging not available - skip messaging features
     // This is common on mobile browsers or guest routes
     this.messaging = null;
   }

    // QSL-10805: sign in to Firebase anonymously so RTDB reads carry `auth != null`
    // (required once the RTDB rules move to ".read": "auth != null"). The web app had
    // NO Firebase Auth at all, so any auth-gated rule would deny every RTDB read.
    //
    // GATED on the same `isFirebaseAuthEnabled` org setting as mobile so it is one
    // universal, server-controlled kill switch: setting OFF -> the web stops
    // authenticating on next load (for rollback, alongside reverting the console rule
    // to ".read": true). Best-effort — never blocks boot; if the setting can't be read
    // we do NOT authenticate (matches the mobile fail-safe).
    this.http
      .get<boolean>(
        `${environment.apiURL}/organizations/${QUPIL_ORGANIZATION_ID}/firebase-auth`
      )
      .subscribe({
        next: (enabled) => {
          if (!enabled) return; // kill switch OFF -> do not authenticate
          try {
            const fbAuth = getAuth();
            onAuthStateChanged(fbAuth, (fbUser) => {
              if (!fbUser) {
                signInAnonymously(fbAuth).catch((e) =>
                  console.warn("Firebase anonymous sign-in failed", e)
                );
              }
            });
          } catch (e) {
            console.warn("Firebase auth init skipped", e);
          }
        },
        error: () => {
          /* could not read the setting -> do not authenticate */
        },
      });

    // Organization and language setup logic
    const baseUrl = window.location.origin;
    if (
      baseUrl.includes("vcfo") &&
      this.authService.getOrganizationId() !== VCFO_ORGANIZATION_ID
    ) {
      this.authService.setOrganizationId(VCFO_ORGANIZATION_ID);
      if (!localStorage.getItem("en")) this.authService.setLanguage("en", VCFO_LANGUAGE_EN);
      window.location.reload();
    } else if (
      !baseUrl.includes("vcfo") &&
      this.authService.getOrganizationId() !== QUPIL_ORGANIZATION_ID
    ) {
      this.authService.setOrganizationId(QUPIL_ORGANIZATION_ID);
      if (!localStorage.getItem("en")) this.authService.setLanguage("en", QUPIL_LANGUAGE_EN);
      window.location.reload();
    }

    // QSL-10245: boot the dual-language (AR/EN) i18n — loads the shared scope
    // from ui_translation and applies the document direction. Resolves the
    // language from the persisted choice (uiLang) or falls back to English.
    //
    // QSL-11027: MUST run BEFORE requestPermission() below. When browser
    // notification permission is denied, requestPermission() opens the
    // NotificationPermissionDialog and AWAITS its dismissal — so with init()
    // after it, the `shared` translation scope was never loaded while the
    // dialog was on screen and every `| translate` pipe in it rendered its RAW
    // KEY (shared_notif_enable_title, …) for the dialog's whole lifetime.
    await this.appI18n.init();

    // Request messaging permissions and subscribe to messaging service.
    // QSL-11027: never inside the RN SirajWebView (QUPIL_MOBILE) — notifications
    // there are native FCM, and a browser-settings instruction dialog is
    // meaningless in an embedded webview.
    if (
      !(window as any).QUPIL_MOBILE &&
      this.authService.getOrganizationId() == QUPIL_ORGANIZATION_ID
    ) {
      // Request permission (errors are handled silently in the service)
      await this.message.requestPermission().catch(() => {
        // Silently handle - permission issues are user choices
      });
    }

    // Listen for incoming messages using the updated method (only if messaging is available)
    if (this.messaging) {
      onMessage(this.messaging, (payload) => {
        this.displayForegroundNotification(payload);
        this.refreshNavbarNotifications();
      });
    }

    // Get images, languages, and icons
    await this.authService.getLogoAndImages(ORGANIZATION_ID).toPromise();
    await this.authService.getLanguages(ORGANIZATION_ID).toPromise();

    // QSL-10245: the teacher's stored user.languageCode is the source of truth for
    // their UI language — seed it ONCE the current user is known so the web honours
    // the same preference as mobile/Siraj. Scoped to TEACHER so the (untranslated)
    // admin/super-admin shells are never flipped to Arabic. One-shot: after the
    // initial seed the in-session toggle/settings selector is authoritative (and
    // persists back), so a later user-refresh emission must not revert that choice.
    let langSeeded = false;
    this.store.pipe(select(getCurrentUser)).subscribe((user: IUser | null) => {
      if (!langSeeded && user?.organizations?.[0]?.role?.type === "TEACHER") {
        langSeeded = true;
        // Default to Arabic when the teacher has no stored languageCode.
        this.appI18n.applyServerLanguage(user.languageCode || "ar");
      }
    });

    var link = document.createElement("link");
    link.setAttribute("rel", "icon");
    link.setAttribute("type", "image/x-icon");
    link.setAttribute("href", localStorage.getItem("logo"));
    document.head.appendChild(link);
  }

  private displayForegroundNotification(payload: any) {
    if (
      typeof Notification === "undefined" ||
      Notification.permission !== "granted"
    ) {
      return;
    }

    const title =
      payload?.notification?.title || payload?.data?.title || "Qupil";

    const notificationOptions: NotificationOptions = {
      body: payload?.notification?.body || payload?.data?.body || "",
      icon:
        payload?.notification?.icon ||
        payload?.data?.icon ||
        "/assets/icons/icon-192x192.png",
      data: {
        clickAction:
          payload?.data?.clickAction ||
          payload?.fcmOptions?.link ||
          window.location.origin,
        ...payload?.data,
      },
    };

    const notification = new Notification(title, notificationOptions);
    notification.onclick = () => {
      notification.close();
      const clickTarget =
        notificationOptions.data?.clickAction || window.location.origin;
      window.open(clickTarget, "_blank");
    };
  }

  private refreshNavbarNotifications() {
    this.store.dispatch(
      loadNotifications({
        payload: {
          organizationId: ORGANIZATION_ID,
          query: this.notificationRefreshQuery,
        },
      })
    );
  }
}
